/**
 * Published layout presets — the wire.
 *
 * `api/presets.ts`'s twin, and it follows the same rules: the base URL is read
 * once, a missing one makes the feature *degrade* rather than error, and a
 * failed read is an empty list. **Publishing throws**, because somebody who
 * clicked Publish and saw nothing would assume it worked.
 *
 * One thing differs and it is the whole reason applying is not in here.
 * `fetchLayout` returns the arrangement as published; turning it into a layout
 * this workspace owns means minting a fresh `Layout.id`, and that is a workspace
 * mutation which belongs with the other workspace mutations
 * (`lib/workspace-actions.ts`), not in the module that knows a URL.
 */

import type { Layout } from "@qufolio/model";

import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";
import { slug } from "./presets";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL);

/** Whether there is a server to ask. Off, no publish control is offered. */
export const layoutsConfigured: boolean = BASE !== "";

/** What a listing carries — everything except the arrangement itself. */
export interface LayoutSummary {
  key: string;
  name: string;
  description: string | null;
  /**
   * Who owns it. `user` is the caller's **own saved template** — a layout kept
   * server-side to start new ones from, distinct from the layouts in their
   * workspace, which are tabs.
   */
  ownerKind: "system" | "org" | "user";
  ownerKey: string | null;
  /** How many chart sections it holds. The server counts it, so a list is cheap. */
  chartCount: number;
  /**
   * A group's published layout: its current version. A member's copy that is
   * behind is replaced on their next load (`lib/group-layouts.ts`). Absent
   * before its first versioned publish, and from an older server.
   */
  version?: number;
  /**
   * The owning group's display name, `JAS` for `grp-jas`. The screen and chart
   * template listings send it; this one does not yet, so it is carried when it
   * arrives and `groupDisplayName` falls back when it does not.
   */
  ownerName?: string;
  /**
   * A virtual group's screener overrides for this layout — *the screener
   * showing `from` shows `to`* (`docs/plans/group-sharing.md`). Sent only to a
   * member of the virtual group that set them, or while authoring as it;
   * applied to the member's copy as it is made or replaced.
   */
  screenOverrides?: ScreenOverride[];
  /** When the current version was published (ISO, UTC) — *Shared with me*'s `v3 · Oct 18`. */
  publishedAt?: string;
  /** It holds the Daily Brief: the one group layout still copied in by itself. */
  briefBoard?: true;
}

/** One screener override: screen keys, `from` as published, `to` instead. */
export interface ScreenOverride {
  from: string;
  to: string;
}

/** An override as the author routes list it — which layout it belongs to. */
export interface LayoutOverride extends ScreenOverride {
  layoutKey: string;
}

/** Overrides off the wire, keeping only well-formed pairs. */
function toOverrides(raw: unknown): ScreenOverride[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const pairs = raw.flatMap((o: unknown) => {
    const { from, to } = (o ?? {}) as { from?: unknown; to?: unknown };
    return typeof from === "string" && typeof to === "string" ? [{ from, to }] : [];
  });
  return pairs.length > 0 ? pairs : undefined;
}

interface WireLayout extends Omit<LayoutSummary, "briefBoard"> {
  isActive?: boolean;
  updatedAt?: string | null;
  briefBoard?: boolean;
  definition?: Partial<Layout>;
}

function toSummary(wire: WireLayout): LayoutSummary {
  return {
    key: wire.key,
    name: wire.name,
    description: wire.description ?? null,
    ownerKind: wire.ownerKind,
    ownerKey: wire.ownerKey ?? null,
    chartCount: typeof wire.chartCount === "number" ? wire.chartCount : 0,
    // **Carried, never dropped.** It was, from 09-29 to 10-04: every row read
    // as unpublished, so `group-layouts.ts` never saw a member's copy fall
    // behind and a republished layout reached nobody.
    ...(typeof wire.version === "number" ? { version: wire.version } : {}),
    ...(typeof wire.ownerName === "string" ? { ownerName: wire.ownerName } : {}),
    // Shared with me prints the day it was published; ● new is the version.
    ...(typeof wire.updatedAt === "string" ? { publishedAt: wire.updatedAt } : {}),
    // The one group layout still copied in by itself (`group-layouts.ts`).
    ...(wire.briefBoard === true ? { briefBoard: true } : {}),
    // Named here for `version`'s reason: this mapper whitelists, and an
    // override it dropped would leave a virtual group's member on Impulse
    // with no error anywhere.
    ...withOverrides(toOverrides(wire.screenOverrides)),
  };
}

function withOverrides(overrides: ScreenOverride[] | undefined): { screenOverrides?: ScreenOverride[] } {
  return overrides ? { screenOverrides: overrides } : {};
}

export async function fetchLayouts(): Promise<LayoutSummary[]> {
  if (!BASE) return [];
  try {
    const response = await fetch(`${BASE}/layouts`, { headers: await requestHeaders() });
    if (!response.ok) return [];
    const body = (await response.json()) as WireLayout[];
    return Array.isArray(body) ? body.map(toSummary) : [];
  } catch {
    return [];
  }
}

/**
 * One layout, whole — the grid, the sync flags and every section.
 *
 * A 404 covers both "no such layout" and "not yours", which the server does not
 * distinguish on purpose so that a key cannot be probed for.
 */
export async function fetchLayout(key: string): Promise<Partial<Layout> | null> {
  if (!BASE) return null;
  try {
    const response = await fetch(`${BASE}/layouts/${encodeURIComponent(key)}`, {
      headers: await requestHeaders(),
    });
    if (!response.ok) return null;
    const wire = (await response.json()) as WireLayout;
    return wire.definition ?? null;
  } catch {
    return null;
  }
}

/**
 * Publish, or replace, one layout. Admin only — the server refuses otherwise.
 *
 * The key is derived from the name, as a preset's is and for the same reason: a
 * club admin naming an arrangement "Morning desk" should not also have to invent
 * a slug, and the collision they would otherwise cause is what
 * `uq_layout_owner_name` refuses — so deriving it makes the two agree instead of
 * failing later.
 *
 * **Only a new layout is slugged.** `key` is the one it is already published
 * under (`publishedKeyFor`): deriving it from the name every time made a
 * renamed layout a second group layout beside the first, which every member
 * then received as well (`docs/plans/group-layout-sync.md`, step 5).
 *
 * **The layout's own `id` is not sent.** The server drops it anyway; not sending
 * it says why here too, where the reader is holding a `Layout` that has one.
 */
export async function publishLayout(input: {
  name: string;
  description: string;
  ownerKey: string;
  layout: Layout;
  key?: string;
  /**
   * The group account to publish as, for one request — **Promote & Publish**
   * from your own account (`docs/features/group-accounts.md`). The server
   * checks it as it checks ☰ › Author as (`_resolve_act_as`).
   */
  actAs?: string;
}): Promise<{ key: string; version?: number }> {
  if (!BASE) throw new Error("no server configured");
  const key = input.key ?? slug(`${input.ownerKey}-${input.name}`);
  const { rows, columns, rowSizes, columnSizes, sync, sections } = input.layout;
  const response = await fetch(`${BASE}/author/layouts/${encodeURIComponent(key)}`, {
    method: "PUT",
    headers: {
      ...(await requestHeaders()),
      ...(input.actAs ? { "X-Act-As": input.actAs } : {}),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name: input.name,
      description: input.description,
      ownerKind: "org",
      ownerKey: input.ownerKey,
      definition: { rows, columns, rowSizes, columnSizes, sync, sections },
    }),
  });
  if (!response.ok) throw new Error((await response.text()) || "publish failed");
  // The version the server recorded — the same number when nothing changed,
  // which is how "No changes since v3" is told apart from a new version.
  const body = (await response.json().catch(() => ({}))) as { version?: number };
  return { key, ...(typeof body.version === "number" ? { version: body.version } : {}) };
}

// ── the caller's own saved layouts ───────────────────────────────────────────
//
// `/layouts` is the read side for all three owner kinds; `/my-layouts` writes
// only your own and does not read `ownerKind` from the body at all — so there
// is no request here that could publish to a club, however this file is edited
// later. The same split `/presets` and `/screens` make.
//
// **These throw**, unlike the reads above: a template that failed to save
// silently is an arrangement somebody expects to find later and will not.

/** Save the current arrangement as one of the caller's own templates. */
export async function saveLayoutTemplate(input: {
  name: string;
  layout: Layout;
}): Promise<LayoutSummary> {
  if (!BASE) throw new Error("no server configured");
  const { rows, columns, rowSizes, columnSizes, sync, sections } = input.layout;
  const response = await fetch(`${BASE}/my-layouts`, {
    method: "POST",
    headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({
      name: input.name,
      definition: { rows, columns, rowSizes, columnSizes, sync, sections },
    }),
  });
  if (!response.ok) throw new Error((await response.text()) || "save failed");
  return (await response.json()) as LayoutSummary;
}

/** Delete one of the caller's own templates. A club's answers 404, which throws. */
export async function deleteLayoutTemplate(key: string): Promise<void> {
  if (!BASE) throw new Error("no server configured");
  const response = await fetch(`${BASE}/my-layouts/${encodeURIComponent(key)}`, {
    method: "DELETE",
    headers: await requestHeaders(),
  });
  if (!response.ok) throw new Error((await response.text()) || "delete failed");
}

// ── a virtual group's screener overrides (docs/plans/group-sharing.md) ───────
//
// Platform admin only, while authoring as the virtual group: the server
// refuses anyone else. The read degrades like every read here; the write
// throws, because a Save that did nothing is an override every member would
// silently miss.

/** Every override the virtual group being authored as has set. */
export async function fetchLayoutOverrides(): Promise<LayoutOverride[]> {
  if (!BASE) return [];
  try {
    const response = await fetch(`${BASE}/author/layout-overrides`, {
      headers: await requestHeaders(),
    });
    if (!response.ok) return [];
    const body = (await response.json()) as unknown;
    if (!Array.isArray(body)) return [];
    return body.flatMap((row: unknown) => {
      const { layoutKey, from, to } = (row ?? {}) as Record<string, unknown>;
      return typeof layoutKey === "string" && typeof from === "string" && typeof to === "string"
        ? [{ layoutKey, from, to }]
        : [];
    });
  } catch {
    return [];
  }
}

/** Set, or with `to: null` remove, the override for one screen in one layout. */
export async function saveLayoutOverride(
  layoutKey: string,
  from: string,
  to: string | null,
): Promise<void> {
  if (!BASE) throw new Error("no server configured");
  const response = await fetch(`${BASE}/author/layout-overrides/${encodeURIComponent(layoutKey)}`, {
    method: "PUT",
    headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({ from, to }),
  });
  if (!response.ok) throw new Error((await response.text()) || "save failed");
}
